/* eslint-disable max-len */
import _ from 'underscore';
import Promise from 'bluebird';

import { parseFileName } from '@bingads-webui-campaign-react/upload-file-picker';
import { FilePayload } from '@bingads-webui/azure-upload';

import {
  CSMLImportSource,
  isRootFolderId,
} from '@bingads-webui-campaign-react/ads-studio-app-shared-components';

import { CSMLSortingRequestParams, parseMTDateString, formatCivilDate } from '../service-utils';
import { transformAssetResponse, transformVideoResponse } from './asset-response-transformers';

import {
  transformAddUploadAssetRequest,
  transformAddStockImageRequest,
  transformAddInvestImageRequest,
  transformAddInvestVideoRequest,
  transformAddUrlImageRequest,
  transformAddImageWithBaseIdRequest,
  transformDeleteAssetRequest,
  transformRenameRequest,
  transformAddUrlVideoRequest,
} from './request-transformers';

import {
  AdsStudioODataError,
  renameFolderApiErrorProcessor,
  bulkMoveApiErrorProcessor,
  bulkMoveApiItemErrorProcessor,
  deleteFolderApiErrorProcessor,
  createFolderApiErrorProcessor,
  bulkDeleteAssetsApiItemErrorProcessor,
  bulkDeleteAssetsApiErrorProcessor
} from '../../error-handling';

const transformParentHierarchy = folder => ({
  folderId: folder.Id,
  folderName: folder.Name,
  parentFolderId: folder.ParentFolderId,
});

const transformFolderResponse = (folder, i18n) => {
  const response = {
    folderId: folder.Id,
    folderName: folder.Name,
    itemCount: folder.ItemsCount,
    createdTime: parseMTDateString(i18n, folder.CreatedTime),
    modifiedTime: parseMTDateString(i18n, folder.ModifiedTime),
    parentFolderId: folder.ParentFolderId,
    assets: _.map(folder.Assets || [], asset => transformAssetResponse(asset, asset.FolderAssetType, i18n)),
    subfolders: _.map(folder.SubFolders || [], subfolder => transformFolderResponse(subfolder, i18n)),
    parentHierarchy: _.map(folder.ParentHierarchy || [], parent => transformParentHierarchy(parent)),
  };
  const perfTimeSeriesData = folder['@ns.performancetimeseries'];
  if (!_.isEmpty(perfTimeSeriesData)) {
    response.perfTimeSeriesData = perfTimeSeriesData;
  }
  return response;
};

const IMPORT_SOURCE_MAP = {
  [CSMLImportSource.Google]: 'GoogleImported',
  [CSMLImportSource.Facebook]: 'FacebookImported',
};

export const getFolderRepository = ({
  folderODataService,
  azureUploader,
  i18n,
  ianaTimeZone,
  timezoneUTCOffset,
  logger,
}) => {
  const getFolderArgs = ({
    perfMetricDateRange,
    searchTerm,
    minWidth,
    minHeight,
    isVideoDurationBoundaryIncluded = false,
    minVideoDuration,
    maxVideoDuration,
    dateAddedStart,
    dateAddedEnd,
    importSource,
    assetSources,
    sortBy,
    skip,
    count,
    folderId,
    assetType,
    includeAssets,
    includeSubfolders,
    includeParentHierarchy,
    assetsToLoadForSubfolders = 1,
    isGlobalSearch = false,
    isAssetPickerScenario = false,
    hasPerfData = false,
    topRated,
    isSemanticSearch = false,
    isRecentSorting = false,
  }) => {
    //
    const filterArgs = {
      searchTerm: searchTerm ? encodeURIComponent(searchTerm.replace(/'/g, "''")) : '',
      minWidth,
      minHeight,
      isVideoDurationBoundaryIncluded,
      minVideoDuration,
      maxVideoDuration,
      dateAddedStart: dateAddedStart && formatCivilDate(i18n, dateAddedStart, ianaTimeZone, timezoneUTCOffset, '00:00:00'),
      dateAddedEnd: dateAddedEnd && formatCivilDate(i18n, dateAddedEnd, ianaTimeZone, timezoneUTCOffset, '23:59:59'),
      assetType,
      sourceType: IMPORT_SOURCE_MAP[importSource],
      hasPerfData,
      topRated,
      assetSources,
    };

    const expandArgs = {
      includeAssets,
      includeSubfolders,
      includeParentHierarchy,
    };

    const args = {
      skip,
      count,
      folderId: isRootFolderId(folderId) ? null : folderId,
      expandArgs,
      filterArgs,
      assetsToLoadForSubfolders,
      isGlobalSearch,
      isAssetPickerScenario,
      perfMetricDateRange,
      isSemanticSearch,
      isRecentSorting,
      ...CSMLSortingRequestParams[sortBy],
    };
    return args;
  };

  const getFolderData = ({
    perfMetricDateRange,
    searchTerm,
    minWidth,
    minHeight,
    isVideoDurationBoundaryIncluded = false,
    minVideoDuration,
    maxVideoDuration,
    dateAddedStart,
    dateAddedEnd,
    importSource,
    assetSources,
    sortBy,
    skip,
    count,
    folderId,
    assetType,
    includeAssets,
    includeSubfolders,
    includeParentHierarchy,
    assetsToLoadForSubfolders = 1,
    isGlobalSearch = false,
    isAssetPickerScenario = false,
    withBIData = false,
    hasPerfData = false,
    topRated = false,
    isSemanticSearch = false,
    isRecentSorting = false,
    perfMarker,
  }) => {
    //

    const args = getFolderArgs({
      perfMetricDateRange,
      searchTerm,
      minWidth,
      minHeight,
      isVideoDurationBoundaryIncluded,
      minVideoDuration,
      maxVideoDuration,
      dateAddedStart,
      dateAddedEnd,
      importSource,
      assetSources,
      sortBy,
      skip,
      count,
      folderId,
      assetType,
      includeAssets,
      includeSubfolders,
      includeParentHierarchy,
      assetsToLoadForSubfolders,
      isGlobalSearch,
      isAssetPickerScenario,
      hasPerfData,
      topRated,
      isSemanticSearch,
      isRecentSorting,
    });
    if (perfMarker) {
      perfMarker.willInit();
    }
    if (withBIData) {
      return folderODataService.getFolderDataWithBIData(args).then((res) => {
        const folder = transformFolderResponse(res || {}, i18n);
        if (perfMarker) {
          perfMarker.done();
        }
        return { folder };
      });
    }

    return folderODataService.getFolderData(args).then((res) => {
      const folder = transformFolderResponse(res || {}, i18n);
      if (perfMarker) {
        perfMarker.done();
      }
      return { folder };
    });
  };

  const getPerfTimeSeriesData = ({
    perfMetricDateRange,
    perfTimeSeriesParams,
    searchTerm,
    minWidth,
    minHeight,
    minVideoDuration,
    maxVideoDuration,
    dateAddedStart,
    dateAddedEnd,
    importSource,
    assetSources,
    assetType,
    isGlobalSearch,
    hasPerfData,
    folderId,
    topRated,
  }) => {
    const filterArgs = {
      searchTerm: searchTerm ? encodeURIComponent(searchTerm.replace(/'/g, "''")) : '',
      minWidth,
      minHeight,
      minVideoDuration,
      maxVideoDuration,
      dateAddedStart: dateAddedStart && formatCivilDate(i18n, dateAddedStart, ianaTimeZone, timezoneUTCOffset, '00:00:00'),
      dateAddedEnd: dateAddedEnd && formatCivilDate(i18n, dateAddedEnd, ianaTimeZone, timezoneUTCOffset, '23:59:59'),
      assetType,
      sourceType: IMPORT_SOURCE_MAP[importSource],
      hasPerfData,
      topRated,
      assetSources,
    };

    return folderODataService.getPerfTimeSeriesData({
      perfMetricDateRange, perfTimeSeriesParams, filterArgs, folderId, isGlobalSearch,
    }).then((res) => {
      const { value } = res || {};
      const perfTimeSeriesData = value || [];
      return { perfTimeSeriesData };
    });
  };

  const createFolder = ({
    folderName,
    parentFolderId,
  }) => {
    const args = {
      folderName,
      folderType: 'Image',
      parentFolderId: isRootFolderId(parentFolderId) ? null : parentFolderId,
    };

    const promise = folderODataService.createFolder(args)
      .catch((err) => {
        throw new AdsStudioODataError({
          message: `create folder api failed, status: ${err.status}`,
          originError: err,
          errorProcessor: createFolderApiErrorProcessor,
        });
      });

    return promise;
  };

  const uploadVideoToAzureBlobWithCancellation = ({
    fileObject,
    onProgress,
  }) => {
    const promise = new Promise((resolve, reject) => {
      const { ext: fileExtension } = parseFileName(fileObject.name);

      folderODataService.getBlobUploadUrl({ fileExtension })
        .then((blob) => {
          const { BlobName } = blob;
          const token = azureUploader.uploadPayloadToBlobWithCancellation({
            blob,
            payload: new FilePayload({ file: fileObject }),
            onProgress,
          });

          resolve({ token, blobName: BlobName });
        })
        .catch(reject);
    });
    return promise;
  };

  const deleteFolder = ({ folderId, folderName }) => {
    const promise = folderODataService.deleteFolder({ folderId }).catch((err) => {
      throw new AdsStudioODataError({
        message: `delete folder api failed, status: ${err.status}`,
        originError: err,
        errorProcessor: deleteFolderApiErrorProcessor,
        folderId,
        folderName,
      });
    });
    return promise;
  };

  const renameFolder = ({ folderId, newFolderName }) => {
    const promise = folderODataService.renameFolder({ folderId, newFolderName })
      .catch((err) => {
        throw new AdsStudioODataError({
          message: `rename folder api failed, status: ${err.status}`,
          originError: err,
          errorProcessor: renameFolderApiErrorProcessor,
        });
      });
    return promise;
  };

  const addFolderUploadAsset = ({
    folderId,
    asset,
    folderAssetType,
    inProgress,
    supportUpscale,
  }) => {
    const args = {
      asset: transformAddUploadAssetRequest(asset, folderAssetType),
      folderId: isRootFolderId(folderId) ? null : folderId,
      inProgress,
      supportUpscale,
    };

    const promise = folderODataService.addAssets(args);

    return promise;
  };

  const addStockImageToFolder = ({
    folderId,
    stockImageId,
  }) => {
    const args = {
      asset: transformAddStockImageRequest({ stockImageId }),
      folderId: isRootFolderId(folderId) ? null : folderId,
    };

    const promise = folderODataService.addAssets(args);

    return promise;
  };

  const addInvestImageToFolder = ({
    folderId,
    investImageId,
    imageSrc,
  }) => {
    const args = {
      asset: transformAddInvestImageRequest({ investImageId, imageSrc }),
      folderId: isRootFolderId(folderId) ? null : folderId,
    };

    const promise = folderODataService.addAssets(args);

    return promise;
  };

  const addInvestVideoToFolder = ({
    folderId,
    investVideoId,
    videoSrc,
    description,
  }) => {
    const args = {
      asset: transformAddInvestVideoRequest({ investVideoId, videoSrc, description }),
      folderId: isRootFolderId(folderId) ? null : folderId,
    };

    const promise = folderODataService.addAssets(args);

    return promise;
  };

  const addUrlImageToFolder = ({
    folderId,
    imageSrc,
    sourceType,
  }) => {
    const args = {
      asset: transformAddUrlImageRequest({ imageSrc, sourceType }),
      folderId: isRootFolderId(folderId) ? null : folderId,
    };

    const promise = folderODataService.addAssets(args);

    return promise;
  };

  const addImageWithBaseIdToFolder = ({
    folderId,
    imageSrc,
    metaData,
    baseId,
    sourceType,
  }) => {
    const args = {
      asset: transformAddImageWithBaseIdRequest({
        imageSrc, metaData, baseId, sourceType,
      }),
      folderId: isRootFolderId(folderId) ? null : folderId,
    };

    const promise = folderODataService.addAssets(args);

    return promise;
  };

  const postVideoByUrl = ({
    videoUrl,
    name,
    folderId,
  }) => {
    const args = {
      asset: transformAddUrlVideoRequest({ videoUrl, name }),
      folderId: isRootFolderId(folderId) ? null : folderId,
    };

    const promise = folderODataService.addAssets(args);

    return promise;
  };

  const updateVideoStatusByIds = (videoTasks) => {
    logger.logTrace('Update video status task length', { taskLength: videoTasks.length });
    return folderODataService.getVideosByIdsAsync(videoTasks).then((response) => {
      if (response && response.length > 0) {
        const transformedRes = response.map(item => transformVideoResponse(item, i18n));
        return transformedRes;
      }
      return [];
    });
  };

  const deleteAsset = ({
    assetId,
    folderAssetType,
  }) => {
    const asset = transformDeleteAssetRequest({ assetId, folderAssetType });
    const promise = folderODataService.deleteAsset(asset);

    return promise;
  };

  const renameAsset = ({
    assetId,
    folderAssetType,
    name,
  }) => {
    const asset = transformRenameRequest({ assetId, folderAssetType, name });
    const promise = folderODataService.updateAsset(asset);

    return promise;
  };

  const batchMove = ({
    destFolderId, assets, folders,
  }) => {
    const assetIds = _.map(assets, ({ folderAssetType, id: assetId }) => transformDeleteAssetRequest({ folderAssetType, assetId }).Asset);
    const folderIds = _.map(folders, ({ folderId }) => folderId);
    const promise = folderODataService.batchMove({
      destFolderId: isRootFolderId(destFolderId) ? null : destFolderId,
      assets: assetIds,
      folders: folderIds,
    }).then((res) => {
      // res only contains items with errors
      const assetsSummary = _.map(assets, (ast) => {
        // eslint-disable-next-line eqeqeq
        const { Success, Errors } = _.find(res.assets, ({ Id }) => Id == ast.id) || { Success: true };
        const ret = { ...ast };
        if (!Success) {
          ret.err = new AdsStudioODataError({ originErrors: Errors, errorProcessor: bulkMoveApiItemErrorProcessor });
        }
        return ret;
      }) || [];
      const foldersSummary = _.map(folders, (fdr) => {
        const { Success, Errors } = _.find(res.folders, ({ Id }) => Id === fdr.folderId) || { Success: true };
        const ret = { ...fdr };
        if (!Success) {
          ret.err = new AdsStudioODataError({ originErrors: Errors, errorProcessor: bulkMoveApiItemErrorProcessor });
        }
        return ret;
      }) || [];
      return { assetsSummary, foldersSummary, hasError: !_.isEmpty(res.assets) || !_.isEmpty(res.folders) };
    }).catch((err) => {
      throw new AdsStudioODataError({
        message: `batch move api failed, status: ${err.status}`,
        originError: err,
        errorProcessor: bulkMoveApiErrorProcessor,
        folderCount: folders ? folders.length : 0,
        assetCount: assets ? assets.length : 0,
      });
    });
    return promise;
  };

  const bulkDeleteAssets = (assets) => {
    const assetsToDelete = _.map(assets, ({ folderAssetType, id: assetId }) => {
      const id = transformDeleteAssetRequest({ folderAssetType, assetId }).Asset.Id;
      return { assetId: id, folderAssetType };
    });
    const promise = folderODataService.bulkDeleteAssets(assetsToDelete).then((res) => {
      // res only contains itmes with errors
      const resultSummary = _.map(assets, (ast) => {
        const bulkupsertErrors = _.filter(res, ({ Id }) => Id === ast.id);
        const ret = { ...ast };
        if (!_.isEmpty(bulkupsertErrors)) {
          ret.err = new AdsStudioODataError({ bulkupsertErrors, errorProcessor: bulkDeleteAssetsApiItemErrorProcessor, assetType: ast.folderAssetType });
        }
        return ret;
      });
      return { resultSummary, hasError: !_.isEmpty(res) };
    }).catch((err) => {
      throw new AdsStudioODataError({
        message: `batch delete api failed, status: ${err.status}`,
        originError: err,
        errorProcessor: bulkDeleteAssetsApiErrorProcessor,
      });
    });
    return promise;
  };

  return {
    getFolderData,
    getFolderArgs,
    getPerfTimeSeriesData,
    createFolder,
    uploadVideoToAzureBlobWithCancellation,
    deleteFolder,
    renameFolder,
    addFolderUploadAsset,
    addStockImageToFolder,
    addInvestImageToFolder,
    addInvestVideoToFolder,
    addUrlImageToFolder,
    addImageWithBaseIdToFolder,
    deleteAsset,
    bulkDeleteAssets,
    renameAsset,
    batchMove,
    postVideoByUrl,
    updateVideoStatusByIds,
  };
};
